import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Leaf, Award } from "lucide-react";
import Reveal from "../components/site/Reveal";
import Seo, { SITE_URL } from "../components/site/Seo";
import {
  IMAGES,
  LOGOS,
  SUPERMARKET_CATEGORIES,
} from "../lib/site-data";

const SUPERMARKET_JSONLD = {
  "@context": "https://schema.org",
  "@type": "GroceryStore",
  name: "Ocean Oasis Supermarket",
  url: `${SITE_URL}/supermarket`,
  description:
    "Premium supermarket at Sundus Plaza, Mogadishu — fresh produce, groceries, beverages, household essentials, personal care and imported specialty goods.",
  telephone: "+252610424241",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Sundus Plaza, Bondhere District",
    addressLocality: "Mogadishu",
    addressCountry: "SO",
  },
  openingHours: "Mo-Su 07:00-22:00",
  parentOrganization: { "@type": "Organization", name: "Ocean Oasis", url: SITE_URL },
};

export default function Supermarket() {
  return (
    <main data-testid="page-supermarket" className="overflow-hidden">
      <Seo
        title="Ocean Oasis Supermarket | Premium Groceries & Fresh Produce in Mogadishu"
        description="A premium supermarket at Sundus Plaza, Bondhere District, Mogadishu — fresh daily produce, trusted brands, imported specialty goods and household essentials. Open every day 7 AM – 10 PM."
        path="/supermarket"
        jsonLd={SUPERMARKET_JSONLD}
      />
      {/* HERO — editorial split */}
      <section className="relative bg-[#fdf9f2]">
        <div className="grid lg:grid-cols-12 min-h-[100vh]">
          <div className="lg:col-span-6 flex items-center pt-32 pb-16 lg:pb-24 px-6 md:px-12 lg:px-16 xl:px-24">
            <div className="max-w-xl w-full">
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="font-label text-[#f59e0b] mb-8 flex items-center gap-3"
              >
                <span className="w-10 h-px bg-[#f59e0b]" />
                Ocean Oasis · Division 02
              </motion.p>
              <motion.img
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                src={LOGOS.supermarket}
                alt="Ocean Oasis Supermarket logo"
                className="w-full max-w-md h-auto object-contain mix-blend-multiply"
              />
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="font-display text-[#0a3d7a] leading-[0.95] mt-8"
                style={{ fontSize: "clamp(2.2rem, 4.5vw, 4rem)" }}
              >
                Premium quality. <em className="italic text-[#f59e0b]">Daily abundance.</em>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="font-body text-[#0a3d7a]/80 text-base md:text-lg leading-relaxed mt-6"
              >
                A curated marketplace at Sundus Plaza — bringing together
                international standards, fresh local produce and the everyday
                essentials of a well-lived Somali home.
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.55 }}
                className="font-label text-[#f59e0b] mt-8"
              >
                Open every day · 7:00 AM – 10:00 PM
              </motion.p>
            </div>
          </div>

          <div className="lg:col-span-6 relative min-h-[55vh] lg:min-h-full overflow-hidden bg-[#0a3d7a]">
            <img
              src={IMAGES.supermarketHeroPortrait}
              alt="Ocean Oasis Supermarket interior — premium imported goods aisle"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#0a3d7a]/5 to-[#0a3d7a]/25" />
          </div>
        </div>
      </section>

      {/* PROMISE BANDS */}
      <section className="py-20 md:py-24 bg-[#0a3d7a] text-[#faf6f0]">
        <div className="container-x">
          <div className="grid md:grid-cols-3 gap-px bg-[rgba(250,246,240,0.15)]">
            {[
              { Icon: Sparkles, t: "Curated Selection", d: "Every shelf hand-picked. No filler, no fillers." },
              { Icon: Leaf, t: "Fresh Daily", d: "Produce sourced and rotated every morning." },
              { Icon: Award, t: "Trusted Brands", d: "Local favorites and certified imports — side by side." },
            ].map((p, i) => (
              <Reveal
                key={p.t}
                delay={i * 0.08}
                className="bg-[#0a3d7a] p-8 md:p-10"
              >
                <p.Icon size={28} className="text-[#f59e0b] mb-6" strokeWidth={1.5} />
                <h3 className="font-display text-2xl md:text-3xl mb-3">{p.t}</h3>
                <p className="font-body text-sm text-[#faf6f0]/80 leading-relaxed">
                  {p.d}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES EDITORIAL */}
      <section className="py-24 md:py-32 bg-[#faf6f0]">
        <div className="container-x">
          <Reveal>
            <div className="grid lg:grid-cols-12 gap-10 mb-16">
              <div className="lg:col-span-6">
                <p className="font-label text-[#f59e0b]">Departments</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#0a3d7a] leading-[1.02]">
                  Everything for the home, <em className="italic">elevated.</em>
                </h2>
              </div>
              <div className="lg:col-span-5 lg:col-start-8 lg:pt-3">
                <p className="font-body text-base text-[#0a3d7a]/70 leading-relaxed">
                  Six departments. One standard. From morning produce to the
                  final household detail, Ocean Oasis Supermarket is built to
                  be the most reliable shelf in the city.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {SUPERMARKET_CATEGORIES.map((c, i) => (
              <Reveal
                key={c.name}
                delay={(i % 3) * 0.1}
                className="group bg-[#fdf9f2] border border-[rgba(10,61,122,0.1)] overflow-hidden"
              >
                <div className="img-zoom aspect-[4/3] overflow-hidden bg-[#0a3d7a]">
                  <img
                    src={c.image}
                    alt={`${c.name} — Ocean Oasis Supermarket, Mogadishu`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-7">
                  <p className="font-label text-[#f59e0b] mb-3">
                    Department · {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl text-[#0a3d7a] mb-3 leading-tight">
                    {c.name}
                  </h3>
                  <p className="font-body text-sm text-[#0a3d7a]/70 leading-relaxed">
                    {c.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMMERSIVE SPLIT */}
      <section className="py-24 md:py-32 bg-[#fdf9f2]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <Reveal className="lg:col-span-6 img-zoom aspect-[4/5] overflow-hidden">
              <img
                src={IMAGES.supermarketAisle}
                alt="Ocean Oasis Supermarket interior — calm, premium shopping experience"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </Reveal>
            <div className="lg:col-span-5 lg:col-start-8">
              <Reveal delay={0.1}>
                <p className="font-label text-[#f59e0b] mb-5">In-Store</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0a3d7a] leading-[1.02] mb-6">
                  A shopping experience designed for <em className="italic">calm.</em>
                </h2>
                <p className="font-body text-lg text-[#0a3d7a]/80 leading-relaxed mb-4">
                  Wide aisles. Clear pricing. Friendly staff. Air-conditioned
                  comfort. Ocean Oasis Supermarket is built not just to be
                  premium in product — but in atmosphere.
                </p>
                <p className="font-body text-base text-[#0a3d7a]/65 leading-relaxed">
                  Whether you're stocking the home for the week or grabbing a
                  single essential, you'll find the visit pleasant — that is
                  the standard.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0a3d7a] text-[#faf6f0]">
        <div className="container-x">
          <Reveal>
            <div className="max-w-3xl">
              <p className="font-label text-[#f59e0b] mb-5">Visit the Store</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] mb-8">
                Find us at <em className="italic">Sundus Plaza</em>, Bondhere District.
              </h2>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="supermarket-cta-visit"
                  className="btn-primary"
                  style={{ background: "#f59e0b", color: "#0a3d7a" }}
                >
                  Plan a Visit <ArrowRight size={16} />
                </Link>
                <Link
                  to="/"
                  data-testid="supermarket-cta-home"
                  className="btn-outline"
                  style={{ borderColor: "#faf6f0", color: "#faf6f0" }}
                >
                  Back to Group
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
